import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Pagination
} from '@mui/material';
import { adminApi } from '../services/api';

interface AdminUser {
  user_id: string;
  email: string;
  nickname: string;
  is_active: boolean;
  is_deleted: boolean;
  role: 'admin' | 'user';
  created_at: string;
  last_login: string;
}

interface UsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // ダイアログ状態
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: 'suspend' | 'activate' | 'delete' | 'role';
    user: AdminUser | null;
  }>({ open: false, type: 'suspend', user: null });
  
  const [reason, setReason] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'user'>('user');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [page]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response: UsersResponse = await adminApi.getUsers({ page, limit: 20 });
      setUsers(response.users);
      setTotalPages(Math.ceil(response.total / 20));
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'ユーザー一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!actionDialog.user) return;

    try {
      setActionLoading(true);
      
      switch (actionDialog.type) {
        case 'suspend':
          await adminApi.suspendUser(actionDialog.user.user_id, reason);
          break;
        case 'activate':
          await adminApi.activateUser(actionDialog.user.user_id);
          break;
        case 'delete':
          await adminApi.deleteUser(actionDialog.user.user_id, reason);
          break;
        case 'role':
          await adminApi.updateUserRole(actionDialog.user.user_id, newRole);
          break;
      }
      
      setActionDialog({ open: false, type: 'suspend', user: null });
      setReason('');
      await fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || '操作に失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  const openActionDialog = (type: 'suspend' | 'activate' | 'delete' | 'role', user: AdminUser) => {
    setActionDialog({ open: true, type, user });
    setNewRole(user.role);
  };

  const getStatusChip = (user: AdminUser) => {
    if (user.is_deleted) {
      return <Chip label="削除済み" color="error" size="small" />;
    }
    if (!user.is_active) {
      return <Chip label="停止中" color="warning" size="small" />;
    }
    return <Chip label="アクティブ" color="success" size="small" />;
  };

  const getRoleChip = (role: string) => {
    return (
      <Chip 
        label={role === 'admin' ? '管理者' : '一般ユーザー'} 
        color={role === 'admin' ? 'primary' : 'default'} 
        size="small" 
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        ユーザー管理
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ニックネーム</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>ロール</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>登録日</TableCell>
              <TableCell>最終ログイン</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.user_id}>
                <TableCell>{user.nickname}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{getRoleChip(user.role)}</TableCell>
                <TableCell>{getStatusChip(user)}</TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString('ja-JP')}
                </TableCell>
                <TableCell>
                  {user.last_login 
                    ? new Date(user.last_login).toLocaleDateString('ja-JP')
                    : '-'
                  }
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {user.is_active && !user.is_deleted && (
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => openActionDialog('suspend', user)}
                      >
                        停止
                      </Button>
                    )}
                    {!user.is_active && !user.is_deleted && (
                      <Button
                        size="small"
                        color="success"
                        onClick={() => openActionDialog('activate', user)}
                      >
                        復活
                      </Button>
                    )}
                    {!user.is_deleted && (
                      <>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => openActionDialog('delete', user)}
                        >
                          削除
                        </Button>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => openActionDialog('role', user)}
                        >
                          ロール変更
                        </Button>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* アクションダイアログ */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, type: 'suspend', user: null })}>
        <DialogTitle>
          {actionDialog.type === 'suspend' && 'ユーザー停止'}
          {actionDialog.type === 'activate' && 'ユーザー復活'}
          {actionDialog.type === 'delete' && 'ユーザー削除'}
          {actionDialog.type === 'role' && 'ロール変更'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            ユーザー: {actionDialog.user?.nickname} ({actionDialog.user?.email})
          </Typography>
          
          {(actionDialog.type === 'suspend' || actionDialog.type === 'delete') && (
            <TextField
              fullWidth
              label="理由"
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              margin="normal"
              required
            />
          )}
          
          {actionDialog.type === 'role' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>新しいロール</InputLabel>
              <Select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as 'admin' | 'user')}
              >
                <MenuItem value="user">一般ユーザー</MenuItem>
                <MenuItem value="admin">管理者</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: 'suspend', user: null })}>
            キャンセル
          </Button>
          <Button 
            onClick={handleAction} 
            disabled={actionLoading || ((actionDialog.type === 'suspend' || actionDialog.type === 'delete') && !reason)}
            color="primary"
          >
            {actionLoading ? <CircularProgress size={20} /> : '実行'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsers;
